import { weeklyLoad, type CommittedJob, type ISODate, type JobStatus, type RiskLevel } from "@clarent/scheduling";
import { weekCommencing } from "../lib/format";

interface Row {
  status: JobStatus;
  schedule: { riskLevel: RiskLevel };
}

/**
 * Design system v2.1 summary strip. Stat tiles in the order At risk, Tight, Can't make it,
 * hidden at zero. Shop hours: one bar per week, this week and the next two.
 */
export function SummaryStrip({
  jobs,
  committed,
  weeklyHours,
  today,
}: {
  jobs: readonly Row[];
  committed: readonly CommittedJob[];
  /** The shop's weekly hours across every active machine (D49). */
  weeklyHours: number;
  today: ISODate;
}) {
  const open = jobs.filter((j) => j.status !== "complete");
  const count = (risk: RiskLevel) => open.filter((j) => j.schedule.riskLevel === risk).length;
  const impossible = open.filter((j) => j.schedule.riskLevel === "impossible");
  const tiles = [
    { n: count("at_risk"), label: "At risk", reason: "Needs to start soon", className: "bg-risk-tint text-risk-text" },
    { n: count("tight"), label: "Tight", reason: "Little slack left", className: "bg-tight-tint text-tight-text" },
    {
      n: impossible.length,
      label: "Can't make it",
      reason: impossible.every((j) => j.status === "quoted") ? "Quote needs a new date" : "Due date can't be met",
      className: "bg-late-tint text-late",
    },
  ].filter((t) => t.n > 0);
  const weeks = weeklyLoad(committed, today, 3);

  return (
    <section className="flex flex-wrap items-center gap-2.5 rounded-panel border border-line bg-bg p-2.5" aria-label="Summary">
      {tiles.map((t) => (
        <div key={t.label} className={`flex items-center gap-3 rounded-[14px] px-4 py-3 ${t.className}`}>
          <span className="heading text-[28px] leading-none">{t.n}</span>
          <span className="flex flex-col gap-px">
            <span className="text-[13px] leading-4 font-extrabold">{t.label}</span>
            <span className="text-xs leading-4 text-muted">{t.reason}</span>
          </span>
        </div>
      ))}
      {tiles.length === 0 && <p className="px-4 py-3 text-[13px] text-muted">Nothing at risk. Every open job can start in time.</p>}
      <div className="flex flex-1 flex-wrap items-center justify-end gap-5 px-3.5 py-2">
        <span className="text-[13px] leading-4 font-extrabold">Shop hours</span>
        {weeks.map((w, i) => {
          const share = weeklyHours > 0 ? w.hours / weeklyHours : 0;
          const colour = share > 1 ? "bg-risk" : share >= 0.9 ? "bg-tight" : "bg-brand";
          return (
            <div key={w.weekStart} className="flex w-[150px] shrink-0 flex-col gap-1.5">
              <div className="flex justify-between font-mono text-[11px] leading-[14px]">
                <span className="font-bold uppercase">{i === 0 ? "This week" : weekCommencing(w.weekStart)}</span>
                <span className="text-muted">
                  {Math.round(w.hours)} / {Math.round(weeklyHours)} h
                </span>
              </div>
              <div className="h-2 rounded-full bg-surface">
                <div className={`h-2 rounded-full ${colour}`} style={{ width: `${Math.min(100, share * 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
